import React, { forwardRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { createCustomSpacing } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { createCSSTransition, createTransition, MotionDurations, MotionDurationsSeconds, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { minimalHitbox, overflowEllipsis } from '@polestar/component-warehouse-react/helpers/style';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import { m as motion } from 'framer-motion';
const Tab = forwardRef(({ id, parentId, label, index = 0, active = false, disabled = false, maxLabelWidth = 140, minLabelWidth, monochrome, onClick, ...props }, ref) => {
    const memoizedId = useSSRSafeId('tab', id);
    const handleClick = useCallback(() => {
        onClick === null || onClick === void 0 ? void 0 : onClick(index);
    }, [onClick, index]);
    return (React.createElement(TabButton, { ...aria({
            selected: active,
            role: 'tab',
            disabled,
        }), id: memoizedId, as: (props === null || props === void 0 ? void 0 : props.href) ? 'a' : 'button', ...props, ref: ref, type: "button", active: active, disabled: disabled, onClick: disabled || typeof handleClick === 'undefined' ? undefined : handleClick, monochrome: monochrome },
        React.createElement(Label, { minLabelWidth: minLabelWidth, maxLabelWidth: maxLabelWidth }, label),
        active && (React.createElement(ActiveBorder, { layout: true, layoutId: parentId || 'active', monochrome: monochrome, transition: createTransition({ duration: MotionDurationsSeconds.slow }), style: {
                /* This prevents framer motion from an initial animation on the y axis */
                originY: '0px',
            } }))));
});
Tab.displayName = 'Tab';
export default Tab;
const Label = styled.span(({ maxLabelWidth, minLabelWidth }) => css(css `
      display: block;
      white-space: nowrap;
      max-width: ${maxLabelWidth
    ? typeof maxLabelWidth === 'number'
        ? `${maxLabelWidth}px`
        : maxLabelWidth
    : 'none'};
      ${overflowEllipsis};
    `, minLabelWidth &&
    css `
        min-width: ${typeof minLabelWidth === 'number' ? `${minLabelWidth}px` : minLabelWidth};
      `));
const TabButton = styled('button', shouldNotForwardProps(true, 'monochrome'))(({ theme, active, disabled, monochrome }) => css(css `
      background: none;
      border: none;
      appearance: none;
      color: ${monochrome ? theme.tabs.monochrome.color : theme.tabs.color};
      display: block;
      padding: ${createCustomSpacing(7 / 16)} 0 ${createCustomSpacing(7 / 16)};
      text-decoration: none;
      transition: ${createCSSTransition('all', MotionEasings.default, MotionDurations.fast)};
      position: relative;
      text-align: center;
      cursor: pointer;
      user-select: none;
      touch-action: manipulation;
      -webkit-tap-highlight-color: transparent;

      @media (hover: hover) {
        &:hover {
          color: ${monochrome ? theme.tabs.monochrome.activeColor : theme.tabs.activeColor};
        }
      }

      &:active {
        color: ${monochrome ? theme.tabs.monochrome.activeColor : theme.tabs.activeColor};
      }
    `, minimalHitbox, active &&
    css `
        color: ${monochrome ? theme.tabs.monochrome.activeColor : theme.tabs.activeColor};
        cursor: default;
      `, disabled &&
    css `
        color: ${monochrome ? theme.tabs.monochrome.disabledColor : theme.tabs.disabledColor};
        cursor: default;
        pointer-events: none;
      `));
const ActiveBorder = styled(motion.div)(({ theme, monochrome }) => css `
    position: absolute;
    inset: auto 0 0;
    block-size: ${createCustomSpacing(2 / 16)};
    background: ${monochrome
    ? theme.tabs.monochrome.borderColorSelected
    : theme.tabs.borderColorSelected};
    z-index: 1;

    @media (min-resolution: 2dppx) {
      block-size: ${createCustomSpacing(1.5 / 16)};
    }
  `);
